import React, {FC, useRef} from 'react';
import styled from 'styled-components';

import {MouseEventHandler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {stretch} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useAvailabilityStatus} from '../../../../../srcCommon/app/availabilityStatusHooks';
import {usePush} from '../../../../../srcCommon/push/pushContext';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../services/tracking';
import {getUser} from '../../../../store/data/teammates/teammatesSelectors';
import {TrackingLocationsEnum} from '../topBarHelpers';
import {UserProfileButton} from './userProfileButton';
import {UserProfileDropdown} from './userProfileDropdown';

/*
 * Styles.
 */

const StyledUserProfileWrapper = styled.div`
  margin-left: 18px;

  ${stretch()}

  // Disable drag in Electron.
  -webkit-app-region: no-drag;
`;

const StyledPopoverCoordinator = styled(PopoverCoordinator)`
  ${stretch()}
`;

/*
 * Component.
 */

export const UserProfile: FC = () => {
  const avatarRef = useRef<HTMLDivElement>(null);

  const user = useAppSelector(getUser);
  const {isConnected} = usePush();

  const availabilityStatus = useAvailabilityStatus();

  const onDoubleClick: MouseEventHandler = (event) => {
    // Don't maximize in Electron.
    event.preventDefault();
  };

  const renderUserProfileButton = (isDropdownOpen: boolean) => (
    <UserProfileButton
      isDropdownOpen={isDropdownOpen}
      avatarRef={avatarRef}
      teammate={user}
      availabilityStatus={availabilityStatus.type}
      isConnected={isConnected}
    />
  );

  const renderUserProfileDropdown = (popoverProps: PopoverTransferProps) => (
    <UserProfileDropdown
      popoverProps={popoverProps}
      teammate={user}
      availabilityStatus={availabilityStatus.type}
      isConnected={isConnected}
    />
  );

  return (
    <StyledUserProfileWrapper onDoubleClick={onDoubleClick}>
      <StyledPopoverCoordinator
        onPopoverToggle={(isOpen) =>
          isOpen &&
          trackInterfaceEvent(InterfaceEventsEnum.USER_PROFILE_CLICK, {location: TrackingLocationsEnum.P0})
        }
        renderButton={(isOpen) => renderUserProfileButton(isOpen)}
        renderModal={(popoverProps) => renderUserProfileDropdown(popoverProps)}
      />
    </StyledUserProfileWrapper>
  );
};
